import {
    SearchEngine,
    Unsubscribe,
    Tab as HeadlessTab,
    buildTab,
    BreadcrumbManager as HeadlessBreadcrumbManager,
    buildBreadcrumbManager,
    SearchBox as HeadlessSearchBox,
    buildSearchBox,
    DidYouMean as HeadlessDidYouMean,
    buildDidYouMean,
    QueryError as HeadlessQueryError,
    buildQueryError,
    QuerySummary as HeadlessQuerySummary,
    buildQuerySummary,
    ResultList as HeadlessResultList,
    buildResultList,
    Facet as HeadlessFacet,
    buildFacet,
    buildDateSortCriterion,
	buildRelevanceSortCriterion,
	Sort as HeadlessSort,
    buildSort,
    SortOrder,
    SortCriterion,
    ResultsPerPage as HeadlessResultsPerPage,
    buildResultsPerPage,
    Pager as HeadlessPager,
    buildPager,
    HistoryManager as HeadlessHistoryManager,
    buildHistoryManager,
    SearchAppState,
    loadSearchAnalyticsActions,
    loadQueryActions,
    StandaloneSearchBoxAnalytics,
    buildQueryExpression,
    RecentQueriesList as HeadlessRecentQueriesList,
    buildRecentQueriesList,
    GeneratedAnswer as HeadlessGeneratedAnswer,
    buildGeneratedAnswer,
} from '@coveo/headless';
import { Component, createRef, RefObject } from 'react';

import { BreadcrumbManager as BreadcrumbManagerFn } from './components/breadcrumb-manager/breadcrumb-manager.fn';
import { Context } from './components/context/context';
import { DidYouMean as DidYouMeanFn } from './components/did-you-mean/did-you-mean.fn';
import { Facet as FacetFn } from './components/facet/facet.fn';
import { Pager } from './components/pager/pager.fn';
import { QueryError as QueryErrorFn } from './components/query-error/query-error.fn';
import { QuerySummary as QuerySummaryFn } from './components/query-summary/query-summary.fn';
import { ResultList } from './components/result-list/result-list.fn';
import { VideoResultList } from './components/result-list/video-result-list.fn';
import { SearchBox as SearchBoxFn } from './components/search-box/search-box.fn';
import { Sort as SortFn } from './components/sort/sort.fn';
import { standaloneSearchBoxStorageKey } from './components/standalone-search-box/standalone-search-box-storage-key';
import { GenerativeAnswerComponent } from './components/generative-answers/generative-answer-component';
import { bindUrlManager } from './components/url-manager/url-manager';
import { AppContext } from './context/engine';
import { Section } from './layout/section';
import React from 'react';
import { DictionaryFieldContext } from './components/dictionary-field-context/dictionary-field-context.fn';
import { bindExecuteTrigger } from './components/triggers/execute-trigger';
import { Tab as TabFn } from './components/tab/tab.fn';
import classNames from 'classnames';

declare global {
    interface Window {
        HEADLESS_STATE?: SearchAppState;
    }
}

const isServerSideRendered = globalThis.window?.HEADLESS_STATE;

const criteria: [ string, SortCriterion ][] = [
    [
        'Relevance',
        buildRelevanceSortCriterion(),
    ],
    [
        'Date (Ascending)',
        buildDateSortCriterion( SortOrder.Ascending ),
    ],
    [
        'Date (Descending)',
        buildDateSortCriterion( SortOrder.Descending ),
    ],
];
const initialCriterion = criteria[ 0 ][ 1 ];

export interface SearchPageProps {
    engine?: SearchEngine;
}

interface advancedQueryFacet {
    name: string;
    field: string;
    type: string;
    label: string;
}

interface paginationProps {
    text?: string;
    mode: string;
    firsttext?: string;
    lasttext?: string;
    prevtext?: string;
    nexttext?: string;
}

interface ParentProps {
    results_layout?: string;
    results_columns?: string;
    results_per_page?: string;
    results_view?: string;
    second_query?: string;
    facets?: Array< advancedQueryFacet >;
    layout?: string;
    pagination: paginationProps;
}

interface facetArrayProps {
    facet: HeadlessFacet;
    label: string;
    name: string;
}

interface SearchPageState {
    page: number;
    mobileFacetsOpen: boolean;
}

export class SearchPage extends Component {
    private engine: SearchEngine;
    private secondEngine: SearchEngine;
    private parentProps: ParentProps;

    private readonly tabs: {
        all: HeadlessTab;
        videos: HeadlessTab;
    };
    private readonly breadcrumbManager: HeadlessBreadcrumbManager;
    private readonly searchBox: HeadlessSearchBox;
    private readonly secondSearchBox: HeadlessSearchBox;
    private readonly didYouMean: HeadlessDidYouMean;
    private readonly queryError: HeadlessQueryError;
    private readonly querySummary: HeadlessQuerySummary;
    private readonly resultList: HeadlessResultList;
    private readonly secondResultList: HeadlessResultList;
    private readonly sort: HeadlessSort;
    private readonly resultsPerPage: HeadlessResultsPerPage;
    private readonly pager: HeadlessPager;
    private readonly secondPager: HeadlessPager;
    private readonly historyManager: HeadlessHistoryManager;
    private readonly recentQueriesList: HeadlessRecentQueriesList;
    private readonly generatedAnswer: HeadlessGeneratedAnswer;

    private unsubscribeUrlManager!: Unsubscribe;

    private unsubscribeExecuteTrigger!: Unsubscribe;
    private static facetArray: Array< facetArrayProps >;

    private resultsRef: RefObject<HTMLDivElement>;

    state: SearchPageState = {
        page: 1,
        mobileFacetsOpen: false,
    }

    constructor( props ) {
        super( props );

        this.initEngine( props );
        this.parentProps = props;
        this.resultsRef = createRef();

        this.tabs = {
            all: buildTab( this.engine, {
                initialState: { isActive: true },
                options: {
                    id: 'all',
                    expression: '',
                },
            } ),
            videos: buildTab( this.engine, {
                options: {
                    id: 'videos',
                    expression: buildQueryExpression()
                        .addStringField( {
                            field: 'documenttype',
                            operator: 'isExactly',
                            values: [ 'Videos' ],
                        } )
                        .toQuerySyntax(),
                },
            } ),
        };

        this.breadcrumbManager = buildBreadcrumbManager( this.engine );

        this.searchBox = buildSearchBox( this.engine, {
            options: { numberOfSuggestions: 8 },
        } );
        this.secondSearchBox = buildSearchBox( this.secondEngine );

        this.generatedAnswer = buildGeneratedAnswer( this.engine, {
            initialState: {
                responseFormat: {
                    contentFormat: [
                        'text/markdown',
                        'text/plain',
                    ],
                },
            },
        } );

        this.didYouMean = buildDidYouMean( this.engine );
        this.queryError = buildQueryError( this.engine );
        this.querySummary = buildQuerySummary( this.engine );

        if ( ! SearchPage.facetArray ) {
            SearchPage.facetArray = [];
            this.parentProps.facets?.forEach( ( facet ) => {
                const { name } = facet;
                const filteredName = name?.replace( /[^a-zA-Z0-9-_]/gm, '' );
                if ( filteredName ) {
                    SearchPage.facetArray.push( {
                        facet: buildFacet(
                            this.engine,
                            {
                                options: {
                                    field: filteredName,
                                    facetId: filteredName,
                                    numberOfValues: 15,
                                },
                            }
                        ),
                        label: facet.label,
                        name: filteredName,
                    } );
                }
            } );
        }

        this.sort = buildSort( this.engine, {
            initialState: { criterion: initialCriterion },
        } );

        const fieldsToInclude = [
            'objecttype',
            'filetype',
            'author',
            'date',
            'documenttype',
            'imagepreview',
        ];
        this.secondResultList = buildResultList( this.secondEngine, {
            options: {
                fieldsToInclude,
            },
        } );

        this.resultList = buildResultList( this.engine, {
            options: {
                fieldsToInclude,
            },
        } );

        this.resultsPerPage = buildResultsPerPage( this.engine, {
            initialState: { numberOfResults: parseInt( props.results_per_page ) ?? 10 },
        } );

        buildResultsPerPage( this.secondEngine, {
            initialState: { numberOfResults: parseInt( props.results_per_page ) ?? 10 },
        } );

        this.pager = buildPager( this.engine, { options: { numberOfPages: 6 } } );
        this.secondPager = buildPager( this.secondEngine, { options: { numberOfPages: 6 } } );
        this.historyManager = buildHistoryManager( this.engine );
        this.recentQueriesList = buildRecentQueriesList( this.engine );
    }

    initEngine( props ) {
        if ( props.engine ) {
            this.engine = props.engine;
            this.secondEngine = props.secondEngine;
            return;
        }
    }

    componentDidMount() {
        // Search parameters, defined in the url's hash, are restored once `UrlManager` is registered.
        // Beware not to restore search parameters until all components are registered,
        // otherwise some components such as facets may not work when the page loads.
        // In this sample, `SearchPage.componentDidMount` happens to be executed after
        // all components are registered, but depending on your implementation this may
        // not be your case.
        this.unsubscribeUrlManager = bindUrlManager( this.engine );

        this.unsubscribeExecuteTrigger = bindExecuteTrigger( this.engine );

        // A search should not be executed until the search parameters are restored.
        this.executeInitialSearch();

        this.secondResultList.subscribe( () => this.setState( { secondResultListState: this.secondResultList.state } ) );
        this.breadcrumbManager.subscribe( () => this.setState( { breadcrumbManagerState: this.breadcrumbManager.state } ) );
    }

    componentWillUnmount() {
        this.unsubscribeUrlManager();
        this.unsubscribeExecuteTrigger();
    }

    componentDidUpdate(prevProps: SearchPageProps, prevState: SearchPageState, snapshot?: any): void {
        if ( prevState.page && this.state.page !== prevState.page && this.resultsRef?.current && this.resultsRef?.current?.getBoundingClientRect().top < 0 ) {
            this.resultsRef.current.scrollIntoView();
        }
    }

    private executeInitialSearch() {
        if ( isServerSideRendered ) {
            const { logInterfaceLoad } = loadSearchAnalyticsActions( this.engine );
            this.engine.dispatch( logInterfaceLoad() );
            return;
        }

        const data = localStorage.getItem( standaloneSearchBoxStorageKey );

        if ( data ) {
            this.executeFirstSearchAfterStandaloneSearchBoxRedirect( data );
            return;
        }

        this.engine.executeFirstSearch();
        this.secondEngine.executeFirstSearch();
    }

    private executeFirstSearchAfterStandaloneSearchBoxRedirect( data: string ) {
        localStorage.removeItem( standaloneSearchBoxStorageKey );
        const parsed: {
            value: string;
            analytics: StandaloneSearchBoxAnalytics;
        } = JSON.parse( data );
        const { value, analytics } = parsed;
        const { updateQuery: q } = loadQueryActions( this.engine );
        const { updateQuery: q2 } = loadQueryActions( this.secondEngine );

        this.engine.dispatch( q( { q: value } ) );
        this.engine.executeFirstSearchAfterStandaloneSearchBoxRedirect( analytics );

        this.secondEngine.dispatch( q2( { q: value } ) );
        this.secondEngine.executeFirstSearchAfterStandaloneSearchBoxRedirect( analytics );
    }

    render() {
        const hasInterrupt = 'true' === this.parentProps.second_query && this?.secondResultList?.state?.hasResults;

        return (
            <AppContext.Provider value={ { engine: this.engine } }>
                <Context />
                <DictionaryFieldContext />
                <SearchBoxFn
                    controller={ this.searchBox }
                    secondController={ hasInterrupt ? this.secondSearchBox : undefined }
                />
                <DidYouMeanFn controller={ this.didYouMean } />
                { this.props['generate-answer'] && (
                    <GenerativeAnswerComponent generatedAnswer={ this.generatedAnswer } />
                ) }
                <div className="coveo-advanced-query__layout alignwide">
                    <div className="coveo-advanced-query__facets">
                        <button
                            className={ classNames(
                                'coveo-advanced-query__facets-toggle',
                                {
                                    'coveo-advanced-query__facets-toggle--open': this.state.mobileFacetsOpen
                                }
                            ) }
                            onClick={ () => this.setState( { mobileFacetsOpen: ! this.state.mobileFacetsOpen } ) }
                        >
                            <span className="coveo-advanced-query__facets-toggle-label">
                                Filters
                                <span className={
                                    classNames(
                                        'coveo-advanced-query__facets-toggle-count',
                                        {
                                            'coveo-advanced-query__facets-toggle-count--active': this.breadcrumbManager.state.hasBreadcrumbs,
                                        }
                                    )
                                }>
                                    { this.breadcrumbManager?.state?.facetBreadcrumbs?.reduce( ( count, facet ) => count + facet.values.length, 0 ) }
                                </span>
                            </span>
                            <svg
                                className="svg coveo-advanced-query__facets-toggle-icon"
                                role="img"
                                focusable="false"
                                height="24"
                                width="24"
                                aria-labelledby="svg-assets-svg-icon-arrow-angle-down-title"
                            >
                                <title id="svg-assets-svg-icon-arrow-right-wide">{ `${ this.state.mobileFacetsOpen ? 'Close' : 'Open' } filters` }</title>
                                <use xlinkHref="#svg-assets-svg-icon-angle-down"></use>
                            </svg>
                        </button>
                        <div
                            className={ classNames(
                                'coveo-advanced-query__active',
                                {
                                    'coveo-advanced-query__active--hidden': ! this.state.mobileFacetsOpen,
                                }
                            ) }
                        >
                            <BreadcrumbManagerFn controller={ this.breadcrumbManager } />
                        </div>
                        {/* <SortFn
                            controller={ this.sort }
                            criteria={ criteria }
                        /> */}
                        <div className={ classNames(
                            'coveo-advanced-query__panels',
                            'panels',
                            'panels--interactive',
                            {
                                'coveo-advanced-query__panels--hidden': ! this.state.mobileFacetsOpen,
                            }
                        ) }>
                            { SearchPage.facetArray.map( ( item, index ) => (
                                <FacetFn
                                    key={ index }
                                    controller={ item.facet as HeadlessFacet }
                                    label={ item.label }
                                    className="coveo-advanced-query__facet"
                                />
                            ) ) }
                        </div>
                    </div>
                    <div
                        className={ classNames(
                            'coveo-advanced-query__results',
                            {
                                [ `coveo-advanced-query__results--${ this.parentProps.results_columns }c` ]: this.parentProps.results_layout === 'grid',
                            }
                        ) }
                        ref={ this.resultsRef }
                    >
                        <QuerySummaryFn controller={ this.querySummary } />
                        <QueryErrorFn controller={ this.queryError } />
                        <ResultList
                            controller={ this.resultList }
                            resultsView={ this.parentProps.results_view }
                            limit={ hasInterrupt ? 5 : undefined }
                        />
                        { ! hasInterrupt && (
                            <Pager
                                mode={ this.parentProps.pagination.mode }
                                attributes={ this.parentProps.pagination }
                                controller={ this.pager }
                                onChange={ ( page ) => this.setState( { page } ) }
                            />
                        ) }
                    </div>
                </div>
                { hasInterrupt && (
                    <>
                        <VideoResultList
                            controller={ this.secondResultList }
                            resultsView={ this.parentProps.results_view }
                        />
                        <div className="coveo-advanced-query__layout alignwide">
                            <div className="coveo-advanced-query__facets" />
                            <div
                                className={ classNames(
                                    'coveo-advanced-query__results',
                                    {
                                        [ ` coveo-advanced-query__results--${ this.parentProps.results_columns }c` ]: this.parentProps.results_layout === 'grid',
                                    }
                                )
                                }
                            >
                                <ResultList
                                    controller={ this.resultList }
                                    resultsView={ this.parentProps.results_view }
                                    limit={ 5 }
                                    offset={ 5 }
                                />
                                <Pager
                                    mode={ this.parentProps.pagination.mode }
                                    attributes={ this.parentProps.pagination }
                                    controller={ this.pager }
                                    onChange={ ( page ) => this.setState( { page } ) }
                                />
                            </div>
                        </div>
                    </>
                ) }
            </AppContext.Provider>
        );
    }
}
